import { Fragment } from 'react';

// Use unified date utils
import { moment } from '../../../utils/dateUtils';

import { calculateTotalPay } from '../../../utils/proposalCalculations';

import {
  Box,
  Table,
  TablePagination,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';

// Icons
import CancelIcon from '@mui/icons-material/Cancel';
import CircleIcon from '@mui/icons-material/Circle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Hooks
import usePagination from '../../../hooks/usePagination';

// Styles
import { table_container_base } from '../../../static/styles';

const statusConfig = {
  'Active': { icon: CircleIcon, color: '#4caf50', bg: '#e8f5e9' },
  'Pending': { icon: CircleIcon, color: '#ff9800', bg: '#fff3e0' },
  'Cold': { icon: CircleIcon, color: '#42a5f5', bg: '#e3f2fd' },
  'Completed': { icon: CheckCircleIcon, color: '#4caf50', bg: '#e8f5e9' },
  'Cancelled': { icon: CancelIcon, color: '#f44336', bg: '#ffebee' },
  'Lost': { icon: CircleIcon, color: '#e91e63', bg: '#fce4ec' },
};

const MobileProposalCard = ({ proposal }) => {
  const status = statusConfig[proposal.status] || statusConfig['Active'];
  const StatusIcon = status.icon;
  const amount = calculateTotalPay(proposal).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  
  return (
    <Card variant="outlined" sx={{ mb: 2, borderLeft: `4px solid ${status.color}` }}>
      <Link href={`/proposals/view/${proposal.id}`} underline="none">
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Chip 
              icon={<StatusIcon sx={{ fontSize: 14 }} />}
              label={proposal.status} 
              size="small" 
              sx={{ bgcolor: status.bg, color: status.color, fontWeight: 600 }} 
            />
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1976d2' }}>
              {proposal.publicId}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <AttachMoneyIcon sx={{ fontSize: 18, color: '#4caf50' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2e7d32' }}>
              {amount}
            </Typography>
            {proposal.additionalNotes?.payment?.completed && (
              <Chip
                icon={<AttachMoneyIcon sx={{ fontSize: 14, color: '#4caf50 !important' }} />}
                label="Paid"
                size="small"
                sx={{ ml: 1, height: 20, backgroundColor: '#e8f5e9', color: '#4caf50', fontWeight: 600, fontSize: 10 }}
              />
            )}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {moment(proposal.createdAt).tz('America/Los_Angeles').format('MM/DD/YYYY')}
            </Typography>
            <ChevronRightIcon sx={{ fontSize: 18, color: '#999' }} />
          </Box>
        </CardContent>
      </Link>
    </Card>
  );
};

const PropertyProposalsTable = ({ proposals }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination();

  return (
    <Fragment>
      {!isMobile && (
        <TableContainer component={Paper} sx={table_container_base}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="20%">Status</TableCell>
                <TableCell width="20%">Public ID</TableCell>
                <TableCell width="20%">Amount</TableCell>
                <TableCell width="20%" align="center">Paid</TableCell>
                <TableCell width="20%">Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {proposals.length ? (
                proposals.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((proposal) => (
                  <TableRow key={proposal.id}>
                    <TableCell>
                      <Link href={`/proposals/view/${proposal.id}`} sx={{ fontWeight: 'bold' }} className="full-col-flex-link" underline="none" color="rgba(0,0,0,0.87)">
                        {proposal.status === 'Active' && <><CircleIcon sx={{ color: 'green', mr: 1, width: 15 }} /> Active</>}
                        {proposal.status === 'Pending' && <><CircleIcon sx={{ color: 'orange', mr: 1, width: 15 }} /> Pending</>}
                        {proposal.status === 'Cold' && <><CircleIcon sx={{ color: '#42a5f5', mr: 1, width: 15 }} /> Cold</>}
                        {proposal.status === 'Completed' && <><CheckCircleIcon sx={{ color: 'green', mr: 1, width: 15 }} /> Completed</>}
                        {proposal.status === 'Cancelled' && <><CancelIcon sx={{ color: 'red', mr: 1, width: 15 }} /> Cancelled</>}
                        {proposal.status === 'Lost' && <><CircleIcon sx={{ color: '#e91e63', mr: 1, width: 15 }} /> Lost</>}
                      </Link>
                    </TableCell>
                    <TableCell><Link href={`/proposals/view/${proposal.id}`} color="rgba(0,0,0,0.87)" underline="none" className="full-col-flex-link">{proposal.publicId}</Link></TableCell>
                    <TableCell><Link href={`/proposals/view/${proposal.id}`} color="rgba(0,0,0,0.87)" underline="none" className="full-col-flex-link">{calculateTotalPay(proposal).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</Link></TableCell>
                    <TableCell align="center">
                      <Link href={`/proposals/view/${proposal.id}`} color="rgba(0,0,0,0.87)" underline="none" className="full-col-flex-link" sx={{ justifyContent: 'center' }}>
                        <AttachMoneyIcon sx={{ color: proposal.additionalNotes?.payment?.completed ? '#4caf50' : '#e0e0e0', fontSize: 24 }} />
                      </Link>
                    </TableCell>
                    <TableCell><Link href={`/proposals/view/${proposal.id}`} color="rgba(0,0,0,0.87)" underline="none" className="full-col-flex-link">{moment(proposal.createdAt).tz('America/Los_Angeles').format('MM/DD/YYYY hh:mm A')}</Link></TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center' }}><strong>No Data</strong></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {isMobile && (
        <Box>
          {proposals.length ? (
            proposals.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((proposal) => (
              <MobileProposalCard key={proposal.id} proposal={proposal} />
            ))
          ) : (
            <Typography variant="body2" sx={{ textAlign: 'center', py: 3 }}>No proposals</Typography>
          )}
        </Box>
      )}

      {!isMobile && (
        <TablePagination rowsPerPageOptions={[10, 25, 50, 100]} component="div" count={proposals.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} labelRowsPerPage="Per page" />
      )}

      {isMobile && proposals.length > rowsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
          <Button size="small" variant="outlined" onClick={() => handleChangePage(null, page - 1)} disabled={page === 0}>Prev</Button>
          <Typography variant="body2" sx={{ alignSelf: 'center' }}>{page + 1}/{Math.ceil(proposals.length / rowsPerPage)}</Typography>
          <Button size="small" variant="outlined" onClick={() => handleChangePage(null, page + 1)} disabled={(page + 1) * rowsPerPage >= proposals.length}>Next</Button>
        </Box>
      )}
    </Fragment>
  );
};

export default PropertyProposalsTable;
